import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus, Sparkles, Handshake, TrendingUp, Building2,
  MapPin, Tag, Heart, X, Star, CheckCircle, Globe,
  LayoutDashboard, Compass, Users, Wallet, GitMerge, LogOut,
  Bell, Search, MessageSquare, BarChart3,
} from "lucide-react";

/* ─── Shared sidebar ─────────────────────────────────── */
const BG = "#0a1628";
const BG_DEEP = "#061020";
const BG_CARD = "rgba(15,30,53,0.85)";
const TEAL = "#14a094";
const TEAL_LT = "#5fcfc0";
const BORDER = "rgba(20,160,148,0.18)";

type SidebarItem = { icon: React.ElementType; label: string; active?: boolean };

const Sidebar = ({ items, role, name, sub }: { items: SidebarItem[]; role: string; name: string; sub: string }) => (
  <div style={{ width: 200, background: BG_DEEP, borderRight: `1px solid ${BORDER}`, display: "flex", flexDirection: "column", padding: "16px 10px", gap: 2, flexShrink: 0 }}>
    {/* Brand */}
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px 14px" }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg,${TEAL},#0f2d52)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>A</span>
      </div>
      <div>
        <div style={{ color: "#e6ecf5", fontSize: 12, fontWeight: 700, letterSpacing: "-0.3px" }}>AdV3nture</div>
        <div style={{ color: TEAL_LT, fontSize: 9, fontWeight: 600, letterSpacing: "0.5px" }}>{sub}</div>
      </div>
    </div>

    <div style={{ fontSize: 9, color: "rgba(150,180,210,0.5)", padding: "0 8px 6px", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>Menu</div>

    {items.map((item) => (
      <div key={item.label} style={{
        display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8,
        background: item.active ? "rgba(20,160,148,0.12)" : "transparent",
        border: item.active ? `1px solid rgba(20,160,148,0.2)` : "1px solid transparent",
        cursor: "default",
      }}>
        <item.icon style={{ width: 13, height: 13, color: item.active ? TEAL_LT : "rgba(150,180,210,0.5)", flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: item.active ? 600 : 400, color: item.active ? TEAL_LT : "rgba(150,180,210,0.55)" }}>{item.label}</span>
        {item.active && <div style={{ marginLeft: "auto", width: 5, height: 5, borderRadius: "50%", background: TEAL, boxShadow: `0 0 6px ${TEAL}` }} />}
      </div>
    ))}

    <div style={{ marginTop: "auto" }}>
      <div style={{ borderRadius: 10, background: `linear-gradient(135deg,rgba(20,160,148,0.18),rgba(20,45,82,0.5))`, border: `1px solid rgba(20,160,148,0.25)`, padding: "10px 12px" }}>
        <div style={{ color: TEAL_LT, fontSize: 10, fontWeight: 700 }}>Upgrade to AA+</div>
        <div style={{ color: "rgba(150,180,210,0.6)", fontSize: 9, marginTop: 2 }}>Unlock all premium features</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 8px 0" }}>
        <div style={{ width: 24, height: 24, borderRadius: "50%", background: `linear-gradient(135deg,${TEAL},#0f2d52)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ color: "#fff", fontSize: 10, fontWeight: 700 }}>{name[0]}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "#e6ecf5", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
          <div style={{ color: "rgba(150,180,210,0.5)", fontSize: 9 }}>{role}</div>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Topbar ─────────────────────────────────────────── */
const Topbar = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
    <div>
      <div style={{ fontSize: 9, color: TEAL_LT, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600, opacity: 0.7 }}>{subtitle}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#e6ecf5", letterSpacing: "-0.5px" }}>{title}</div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ background: "rgba(20,160,148,0.08)", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "5px 10px", display: "flex", alignItems: "center", gap: 6 }}>
        <Search style={{ width: 11, height: 11, color: "rgba(150,180,210,0.4)" }} />
        <span style={{ fontSize: 10, color: "rgba(150,180,210,0.4)" }}>Search…</span>
      </div>
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg,${TEAL},#0f2d52)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>S</span>
      </div>
    </div>
  </div>
);

/* ─── App shell wrapper ──────────────────────────────── */
const AppShell = ({ sidebar, children }: { sidebar: React.ReactNode; children: React.ReactNode }) => (
  <div style={{ display: "flex", height: 400, background: BG, overflow: "hidden", borderRadius: 14, border: `1px solid rgba(20,160,148,0.22)`, boxShadow: `0 0 50px rgba(20,160,148,0.1)`, fontFamily: "'Inter Tight', Inter, sans-serif" }}>
    {sidebar}
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {children}
    </div>
  </div>
);

/* ─── Founder: Profile setup ─────────────────────────── */
const FounderProfileMockup = () => (
  <AppShell
    sidebar={<Sidebar name="Sena Takele" sub="FOUNDER · AA" role="Founder" items={[
      { icon: LayoutDashboard, label: "Dashboard" },
      { icon: Compass, label: "Discover" },
      { icon: Users, label: "Connections" },
      { icon: Wallet, label: "Wallet" },
    ]} />}
  >
    <Topbar title="Complete Your Profile" subtitle="Pages / Profile" />
    <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Progress */}
      <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ color: "#e6ecf5", fontSize: 12, fontWeight: 600 }}>Profile completion</span>
          <span style={{ color: TEAL_LT, fontSize: 11, fontWeight: 700 }}>55%</span>
        </div>
        <div style={{ height: 4, background: "rgba(20,160,148,0.15)", borderRadius: 4, overflow: "hidden" }}>
          <motion.div initial={{ width: 0 }} animate={{ width: "55%" }} transition={{ delay: 0.4, duration: 1.2, ease: "easeOut" }}
            style={{ height: "100%", background: `linear-gradient(90deg,${TEAL},${TEAL_LT})`, borderRadius: 4, boxShadow: `0 0 8px ${TEAL}` }} />
        </div>
      </div>
      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {[
          { label: "Company", value: "NovaPay", done: true },
          { label: "Sector", value: "FinTech · B2B Payments", done: true },
          { label: "Stage", value: "Seed", done: true },
          { label: "Raise target", value: "$2.5M", done: true },
          { label: "Bio (300+ chars)", value: "Add your story…", done: false },
          { label: "LinkedIn URL", value: "linkedin.com/in/…", done: false },
        ].map((f) => (
          <div key={f.label} style={{ display: "flex", alignItems: "center", gap: 10, background: BG_CARD, border: `1px solid ${f.done ? BORDER : "rgba(150,180,210,0.1)"}`, borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: f.done ? TEAL : "rgba(150,180,210,0.25)", flexShrink: 0, boxShadow: f.done ? `0 0 5px ${TEAL}` : "none" }} />
            <span style={{ flex: 1, fontSize: 11, color: "rgba(150,180,210,0.6)" }}>{f.label}</span>
            <span style={{ fontSize: 11, fontWeight: f.done ? 600 : 400, color: f.done ? "#e6ecf5" : "rgba(150,180,210,0.3)" }}>{f.value}</span>
          </div>
        ))}
      </div>
      <button style={{ marginTop: 4, padding: "9px 0", borderRadius: 8, background: TEAL, color: "#061020", fontSize: 11, fontWeight: 700, border: "none", cursor: "default", boxShadow: `0 0 20px rgba(20,160,148,0.35)` }}>
        Complete Profile →
      </button>
    </div>
  </AppShell>
);

/* ─── Founder: Discover investors ───────────────────── */
const FounderDiscoverMockup = () => (
  <AppShell
    sidebar={<Sidebar name="Sena Takele" sub="FOUNDER · AA" role="Founder" items={[
      { icon: LayoutDashboard, label: "Dashboard" },
      { icon: Compass, label: "Discover", active: true },
      { icon: Users, label: "Connections" },
      { icon: Wallet, label: "Wallet" },
    ]} />}
  >
    <Topbar title="Discover Investors" subtitle="Pages / Discover" />
    <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Filter chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {["Seed Stage", "FinTech", "Europe", "£1–5M"].map(tag => (
          <span key={tag} style={{ fontSize: 9, padding: "3px 10px", borderRadius: 20, background: `rgba(20,160,148,0.1)`, border: `1px solid rgba(20,160,148,0.25)`, color: TEAL_LT, fontWeight: 600 }}>{tag}</span>
        ))}
        <span style={{ fontSize: 9, padding: "3px 10px", borderRadius: 20, background: "transparent", border: "1px solid rgba(150,180,210,0.2)", color: "rgba(150,180,210,0.5)", fontWeight: 600 }}>+ Filter</span>
      </div>

      {/* Swipe card */}
      <div style={{ position: "relative", height: 200 }}>
        {/* back card */}
        <div style={{ position: "absolute", inset: "8px 12px 0", background: "rgba(15,30,53,0.5)", border: `1px solid ${BORDER}`, borderRadius: 12, transform: "scale(0.95)", opacity: 0.4 }} />
        {/* front card */}
        <div style={{ position: "absolute", inset: "0 0 8px", background: BG_CARD, border: `1px solid rgba(20,160,148,0.25)`, borderRadius: 12, padding: "16px", boxShadow: "0 8px 30px rgba(0,0,0,0.35)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: `linear-gradient(135deg,rgba(20,160,148,0.25),rgba(20,45,82,0.5))`, border: `1px solid rgba(20,160,148,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <TrendingUp style={{ width: 18, height: 18, color: TEAL_LT }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#e6ecf5", letterSpacing: "-0.3px" }}>Meridian Ventures</div>
              <div style={{ fontSize: 10, color: "rgba(150,180,210,0.6)", marginTop: 1 }}>Seed · FinTech · Europe</div>
            </div>
            <div style={{ background: `rgba(20,160,148,0.15)`, border: `1px solid rgba(20,160,148,0.3)`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: TEAL_LT }}>94% fit</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
            {["B2B Payments", "Africa", "API-first", "£2–5M tickets"].map(t => (
              <span key={t} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 20, background: `rgba(20,160,148,0.08)`, border: `1px solid rgba(20,160,148,0.18)`, color: TEAL_LT }}>{t}</span>
            ))}
          </div>
          <p style={{ fontSize: 10, color: "rgba(150,180,210,0.55)", lineHeight: 1.5 }}>Backing early-stage fintech founders disrupting payments in emerging markets. 14 portfolio companies.</p>
        </div>
      </div>

      {/* Action row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 4 }}>
        <button style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X style={{ width: 16, height: 16, color: "#ef4444" }} />
        </button>
        <button style={{ width: 52, height: 52, borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 25px rgba(20,160,148,0.45)` }}>
          <Heart style={{ width: 22, height: 22, color: "#061020", fill: "#061020" }} />
        </button>
        <button style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(95,207,192,0.1)", border: `1px solid rgba(95,207,192,0.25)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Star style={{ width: 16, height: 16, color: TEAL_LT }} />
        </button>
      </div>
    </div>
  </AppShell>
);

/* ─── Founder: Match + chat ──────────────────────────── */
const FounderMatchMockup = () => (
  <AppShell
    sidebar={<Sidebar name="Sena Takele" sub="FOUNDER · AA" role="Founder" items={[
      { icon: LayoutDashboard, label: "Dashboard" },
      { icon: Compass, label: "Discover" },
      { icon: MessageSquare, label: "Chats", active: true },
      { icon: Users, label: "Connections" },
      { icon: Wallet, label: "Wallet" },
    ]} />}
  >
    <Topbar title="NovaPay × Meridian Ventures" subtitle="Chats / Active" />
    <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "14px 24px", gap: 10 }}>
      {/* Match badge */}
      <div style={{ background: `rgba(20,160,148,0.1)`, border: `1px solid rgba(20,160,148,0.25)`, borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 2 }}
          style={{ width: 22, height: 22, borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 12px ${TEAL}`, flexShrink: 0 }}>
          <CheckCircle style={{ width: 13, height: 13, color: "#061020" }} />
        </motion.div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEAL_LT }}>Mutual Match</div>
          <div style={{ fontSize: 9, color: "rgba(150,180,210,0.55)" }}>Seed · £2M raise · London, UK</div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, justifyContent: "flex-end" }}>
        {[
          { from: "Meridian", text: "Excited to connect! Can you share your latest deck?", mine: false, delay: 0 },
          { from: "NovaPay", text: "Absolutely, sending it over via the deal room now.", mine: true, delay: 0.15 },
          { from: "Meridian", text: "Perfect. Let's schedule a call this week.", mine: false, delay: 0.3 },
          { from: "NovaPay", text: "Thursday works, I'll send a calendar invite.", mine: true, delay: 0.45 },
        ].map((msg, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: msg.delay }}
            style={{ display: "flex", justifyContent: msg.mine ? "flex-end" : "flex-start" }}>
            <div style={{ fontSize: 11, padding: "8px 12px", borderRadius: 10, maxWidth: "72%", lineHeight: 1.4,
              background: msg.mine ? TEAL : "rgba(15,30,53,0.9)",
              color: msg.mine ? "#061020" : "#e6ecf5",
              border: msg.mine ? "none" : `1px solid ${BORDER}`,
              fontWeight: msg.mine ? 500 : 400,
            }}>{msg.text}</div>
          </motion.div>
        ))}
      </div>

      {/* Input bar */}
      <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1, fontSize: 11, color: "rgba(150,180,210,0.3)" }}>Type a message…</span>
        <button style={{ padding: "4px 12px", borderRadius: 6, background: TEAL, color: "#061020", fontSize: 10, fontWeight: 700, border: "none" }}>Send</button>
      </div>
    </div>
  </AppShell>
);

/* ─── Investor: Thesis setup ─────────────────────────── */
const InvestorThesisMockup = () => (
  <AppShell
    sidebar={<Sidebar name="Aman Mahtani" sub="INVESTOR · AA+" role="Investor" items={[
      { icon: LayoutDashboard, label: "Dashboard" },
      { icon: Compass, label: "Discover" },
      { icon: Users, label: "Connections" },
      { icon: Wallet, label: "Wallet" },
    ]} />}
  >
    <Topbar title="Investment Thesis" subtitle="Pages / Profile" />
    <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
      <p style={{ fontSize: 11, color: "rgba(150,180,210,0.6)", lineHeight: 1.5 }}>Tell us what you back, we'll use this to surface the right founders for you.</p>
      {[
        { label: "Fund / Name", value: "Meridian Ventures" },
        { label: "Ticket Size", value: "$500K – $3M" },
        { label: "Stage Focus", value: "Pre-Seed · Seed" },
        { label: "Primary Sectors", value: "FinTech · HealthTech" },
        { label: "Check-in Geography", value: "Europe · UK · Africa" },
      ].map((f) => (
        <div key={f.label} style={{ display: "flex", alignItems: "center", gap: 10, background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "9px 12px" }}>
          <span style={{ flex: 1, fontSize: 10, color: "rgba(150,180,210,0.55)" }}>{f.label}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#e6ecf5" }}>{f.value}</span>
        </div>
      ))}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
        {["Europe", "UK", "Africa", "Remote"].map(g => (
          <span key={g} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, padding: "4px 10px", borderRadius: 20, background: "rgba(59,159,204,0.1)", border: "1px solid rgba(59,159,204,0.25)", color: "#6bc5e8", fontWeight: 600 }}>
            <Globe style={{ width: 9, height: 9 }} />{g}
          </span>
        ))}
      </div>
      <button style={{ marginTop: 6, padding: "9px 0", borderRadius: 8, background: "#3b9fcc", color: "#061020", fontSize: 11, fontWeight: 700, border: "none", cursor: "default", boxShadow: "0 0 20px rgba(59,159,204,0.3)" }}>
        Save & Start Discovering →
      </button>
    </div>
  </AppShell>
);

/* ─── Investor: Browse deal flow ─────────────────────── */
const InvestorDiscoverMockup = () => (
  <AppShell
    sidebar={<Sidebar name="Aman Mahtani" sub="INVESTOR · AA+" role="Investor" items={[
      { icon: LayoutDashboard, label: "Dashboard" },
      { icon: Compass, label: "Discover", active: true },
      { icon: Users, label: "Connections" },
      { icon: Wallet, label: "Wallet" },
    ]} />}
  >
    <Topbar title="Browse Startups" subtitle="Pages / Discover" />
    <div style={{ flex: 1, overflowY: "auto", padding: "14px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ display: "flex", gap: 5 }}>
          {["All", "FinTech", "HealthTech", "ClimaTech"].map((t, i) => (
            <span key={t} style={{ fontSize: 9, padding: "3px 9px", borderRadius: 20, fontWeight: 600,
              background: i === 0 ? TEAL : "transparent",
              color: i === 0 ? "#061020" : "rgba(150,180,210,0.5)",
              border: i === 0 ? "none" : "1px solid rgba(150,180,210,0.2)",
            }}>{t}</span>
          ))}
        </div>
        <span style={{ fontSize: 9, background: "rgba(20,160,148,0.1)", border: `1px solid rgba(20,160,148,0.2)`, borderRadius: 20, padding: "3px 9px", color: TEAL_LT, fontWeight: 600 }}>8 new</span>
      </div>
      {[
        { name: "NovaPay", sector: "FinTech · Seed", raise: "$2.5M", arr: "$180K ARR", fit: 97, color: TEAL },
        { name: "HealthLynk", sector: "HealthTech · Pre-Seed", raise: "$800K", arr: "Beta", fit: 88, color: "#6bc5e8" },
        { name: "CarbonOS", sector: "ClimaTech · Seed", raise: "$1.5M", arr: "$60K ARR", fit: 81, color: TEAL },
        { name: "Kora AI", sector: "FinTech · Pre-Seed", raise: "$500K", arr: "Waitlist", fit: 74, color: "#6bc5e8" },
      ].map((s, i) => (
        <motion.div key={s.name} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
          style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg,rgba(20,160,148,0.2),rgba(20,45,82,0.5))`, border: `1px solid rgba(20,160,148,0.2)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Building2 style={{ width: 14, height: 14, color: TEAL_LT }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#e6ecf5" }}>{s.name}</div>
            <div style={{ fontSize: 9, color: "rgba(150,180,210,0.55)", marginTop: 1 }}>{s.sector} · {s.arr}</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#e6ecf5" }}>{s.raise}</div>
            <div style={{ fontSize: 9, color: s.color, fontWeight: 600, marginTop: 1 }}>{s.fit}% fit</div>
          </div>
        </motion.div>
      ))}
    </div>
  </AppShell>
);

/* ─── Investor: Pipeline dashboard ──────────────────── */
const InvestorPipelineMockup = () => (
  <AppShell
    sidebar={<Sidebar name="Aman Mahtani" sub="INVESTOR · AA+" role="Investor" items={[
      { icon: LayoutDashboard, label: "Dashboard", active: true },
      { icon: Compass, label: "Discover" },
      { icon: Users, label: "Connections" },
      { icon: Wallet, label: "Wallet" },
    ]} />}
  >
    <Topbar title="Welcome back, Aman" subtitle="Pages / Dashboard" />
    <div style={{ flex: 1, overflowY: "auto", padding: "14px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {[
          { label: "Profile Views", value: "24" },
          { label: "New Founders", value: "11" },
          { label: "Active Chats", value: "5" },
        ].map((s) => (
          <div key={s.label} style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, color: "rgba(150,180,210,0.5)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, background: `linear-gradient(135deg,${TEAL_LT},${TEAL})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Pipeline */}
      <div style={{ fontSize: 10, color: "rgba(150,180,210,0.5)", textTransform: "uppercase", letterSpacing: "0.8px", fontWeight: 600 }}>Active pipeline</div>
      {[
        { name: "NovaPay", stage: "Term Sheet", progress: 85, teal: true },
        { name: "HealthLynk", stage: "Due Diligence", progress: 55, teal: false },
        { name: "CarbonOS", stage: "First Call", progress: 25, teal: true },
      ].map((d) => (
        <div key={d.name} style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#e6ecf5" }}>{d.name}</div>
            <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
              background: d.teal ? `rgba(20,160,148,0.12)` : "rgba(59,159,204,0.12)",
              color: d.teal ? TEAL_LT : "#6bc5e8",
              border: `1px solid ${d.teal ? "rgba(20,160,148,0.25)" : "rgba(59,159,204,0.25)"}`,
            }}>{d.stage}</span>
          </div>
          <div style={{ height: 3, background: "rgba(20,160,148,0.1)", borderRadius: 3, overflow: "hidden" }}>
            <motion.div initial={{ width: 0 }} animate={{ width: `${d.progress}%` }}
              transition={{ delay: 0.5 + d.progress * 0.005, duration: 1, ease: "easeOut" }}
              style={{ height: "100%", background: d.teal ? `linear-gradient(90deg,${TEAL},${TEAL_LT})` : "#6bc5e8", borderRadius: 3 }} />
          </div>
        </div>
      ))}
    </div>
  </AppShell>
);

/* ─── Live investor app iframe ───────────────────────── */
const InvestorLiveApp = () => (
  <div className="rounded-2xl overflow-hidden"
    style={{ border: "1px solid rgba(20,160,148,0.28)", boxShadow: "0 0 60px rgba(20,160,148,0.12), 0 16px 40px rgba(0,0,0,0.4)" }}>
    {/* Browser chrome */}
    <div className="flex items-center gap-1.5 px-4 py-2.5"
      style={{ background: "#061020", borderBottom: "1px solid rgba(20,160,148,0.18)" }}>
      <span className="w-2 h-2 rounded-full" style={{ background: "rgba(239,68,68,0.5)" }} />
      <span className="w-2 h-2 rounded-full" style={{ background: "rgba(234,179,8,0.5)" }} />
      <span className="w-2 h-2 rounded-full" style={{ background: "rgba(20,160,148,0.6)" }} />
      <div className="ml-3 flex items-center gap-2 bg-white/5 rounded px-3 py-0.5">
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#14a094" }} />
        <span className="text-[10px] font-mono" style={{ color: "rgba(150,180,210,0.55)" }}>app.adv3nture.io · Investor view</span>
      </div>
      <a href="/investor-app.html" target="_blank" rel="noopener noreferrer"
        className="ml-auto text-[9px] flex items-center gap-1 px-2 py-1 rounded font-medium"
        style={{ color: "#5fcfc0", background: "rgba(20,160,148,0.1)", border: "1px solid rgba(20,160,148,0.2)" }}>
        ↗ Full screen
      </a>
    </div>
    {/* Scaled iframe */}
    <div className="relative overflow-hidden" style={{ height: 420 }}>
      <iframe
        src="/investor-app.html"
        title="AdV3nture investor app, interactive"
        className="border-0 absolute top-0 left-0"
        style={{
          width: "160%", height: "160%",
          transform: "scale(0.625)", transformOrigin: "top left",
          background: "#0a1628",
        }}
        sandbox="allow-scripts allow-same-origin"
      />
      <div className="absolute bottom-0 inset-x-0 h-12 pointer-events-none"
        style={{ background: "linear-gradient(transparent, #0a1628)" }} />
    </div>
    <div className="px-4 py-2.5 flex items-center justify-between"
      style={{ background: "#061020", borderTop: "1px solid rgba(20,160,148,0.12)" }}>
      <span className="text-[10px]" style={{ color: "rgba(150,180,210,0.4)" }}>Click through the sidebar to explore</span>
      <span className="text-[10px] font-medium" style={{ color: "#5fcfc0" }}>● Live preview</span>
    </div>
  </div>
);

/* ─── Main component ─────────────────────────────────── */
const founderSteps = [
  { label: "Build your profile", description: "Share your stage, sector, traction, and raise target. Two minutes to set up. Sets the foundation for every match.", mockup: <FounderProfileMockup /> },
  { label: "Discover investors", description: "Browse investors matched to your round. Swipe right on ones you want to meet, skip the rest instantly. No time wasted.", mockup: <FounderDiscoverMockup /> },
  { label: "Connect and close", description: "When both sides match, your chat unlocks. Share your deck, answer questions, move the deal forward, all in one place.", mockup: <FounderMatchMockup /> },
];

const investorSteps = [
  { label: "Set your thesis", description: "Define your stage, sector, ticket size, and geography. Every founder we surface matches your actual criteria." },
  { label: "Browse deal flow", description: "Review ranked founders in your thesis. Click Discover in the sidebar to see your personalised deal flow." },
  { label: "Track your pipeline", description: "Manage deals from first look to term sheet. Dashboard shows profile views, connections, and active conversations." },
];

const HowItWorks = () => {
  const [role, setRole] = useState<"founder" | "investor">("founder");
  const [activeStep, setActiveStep] = useState(0);

  const founderActive = founderSteps[activeStep];
  const handleRole = (r: "founder" | "investor") => { setRole(r); setActiveStep(0); };

  return (
    <section id="how-it-works" className="py-24 md:py-32 section-teal-strong">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: "rgba(95,207,192,0.8)" }}>How It Works</p>
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            From sign-up to funded{" "}
            <span className="text-gradient">in three steps</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Pick your role and explore the real product, no sign-up required.
          </p>
        </motion.div>

        {/* Role toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-2xl p-1.5 gap-1"
            style={{ border: "1px solid rgba(20,160,148,0.2)", background: "rgba(15,30,53,0.6)" }}>
            {(["founder", "investor"] as const).map((r) => (
              <button key={r} onClick={() => handleRole(r)}
                className="px-7 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300"
                style={role === r ? {
                  background: r === "founder" ? "#14a094" : "#3b9fcc",
                  color: "#061020",
                  boxShadow: `0 0 20px ${r === "founder" ? "rgba(20,160,148,0.4)" : "rgba(59,159,204,0.35)"}`,
                } : { color: "rgba(150,180,210,0.55)" }}>
                {r === "founder" ? "I'm a Founder" : "I'm an Investor"}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={role} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.28 }}
            className={`mx-auto gap-10 items-start ${role === "investor" ? "max-w-5xl grid md:grid-cols-2" : "max-w-5xl grid md:grid-cols-2"}`}>

            {/* Steps */}
            <div className="space-y-3">
              {(role === "founder" ? founderSteps : investorSteps).map((step, i) => {
                const isActive = activeStep === i;
                return (
                  <button key={step.label} onClick={() => setActiveStep(i)}
                    className="w-full text-left rounded-2xl p-5 transition-all duration-300"
                    style={{
                      background: isActive ? "rgba(20,160,148,0.08)" : BG_CARD,
                      border: isActive
                        ? `1px solid rgba(${role === "founder" ? "20,160,148" : "59,159,204"},0.35)`
                        : "1px solid rgba(20,160,148,0.12)",
                      boxShadow: isActive ? `0 0 24px rgba(20,160,148,0.1)` : "none",
                    }}>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 text-xs font-bold font-mono transition-all"
                        style={isActive ? {
                          background: role === "founder" ? "#14a094" : "#3b9fcc",
                          color: "#061020",
                          boxShadow: `0 0 12px rgba(20,160,148,0.5)`,
                        } : { background: "rgba(20,160,148,0.1)", color: "rgba(150,180,210,0.4)" }}>
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-semibold text-sm mb-1"
                          style={{ color: isActive ? "#e6ecf5" : "rgba(150,180,210,0.55)" }}>{step.label}</p>
                        <AnimatePresence>
                          {isActive && (
                            <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                              className="text-xs leading-relaxed overflow-hidden" style={{ color: "rgba(150,180,210,0.6)" }}>
                              {step.description}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* Extra CTA for investor tab */}
              {role === "investor" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                  className="rounded-2xl p-5 text-sm"
                  style={{ background: "rgba(20,160,148,0.06)", border: "1px solid rgba(20,160,148,0.2)" }}>
                  <p className="font-semibold mb-1" style={{ color: "#5fcfc0" }}>Try it now →</p>
                  <p className="text-xs leading-relaxed" style={{ color: "rgba(150,180,210,0.55)" }}>
                    Click through the live app on the right. Navigate the sidebar, explore Discover, and see how deal flow works, no account needed.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Right panel */}
            <div className="relative flex items-start">
              {role === "founder" ? (
                <AnimatePresence mode="wait">
                  <motion.div key={activeStep}
                    initial={{ opacity: 0, scale: 0.97, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: -12 }}
                    transition={{ duration: 0.28 }} className="w-full">
                    {founderSteps[activeStep].mockup}
                  </motion.div>
                </AnimatePresence>
              ) : (
                <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="w-full">
                  <InvestorLiveApp />
                </motion.div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HowItWorks;
